/**
 * Where a session stands. The app renders from this and nothing else:
 *
 * - `booting`     a stored refresh token is being exchanged for an access token
 * - `signedIn`    there is a session; requests carry its access token
 * - `signedOut`   there is no session; `endReason` says how the last one ended
 * - `unreachable` boot could not reach the server; the stored token is kept
 *                 and the admin can retry, rather than being sent to sign in
 */
export const SESSION_STATUS = Object.freeze({
  BOOTING: 'booting',
  SIGNED_IN: 'signedIn',
  SIGNED_OUT: 'signedOut',
  UNREACHABLE: 'unreachable',
});

/** Why the session ended — every way out is named here. */
export const SESSION_END_REASON = Object.freeze({
  /** There never was a session on this device. */
  NONE: 'none',
  /** The admin logged out. */
  LOGGED_OUT: 'loggedOut',
  /** The refresh endpoint refused the refresh token (401/403). */
  EXPIRED: 'expired',
  /** The stored session was minted by a build whose token key no longer matches. */
  BUILD_CHANGED: 'buildChanged',
  /** Another tab logged out. */
  OTHER_TAB: 'otherTab',
});

/**
 * The two localStorage keys that hold a session between reloads. Nothing
 * outside `sessionStore` reads or writes them.
 */
export const SESSION_STORAGE_KEYS = Object.freeze({
  REFRESH_TOKEN: 'refresh_token',
  BUILD_KEY: 'validTokenUuid',
});

/**
 * Access tokens are treated as expired this long before the server would
 * reject them, so a request is never sent with a token about to lapse in flight.
 */
export const ACCESS_TOKEN_EXPIRY_MARGIN_MS = 10_000;
